import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/config";

function getCsrfToken(): string {
  return (
    document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith("csrftoken="))
      ?.split("=")[1] ?? ""
  );
}

type Method = "email" | "phone" | "msteams" | "webhook";
type Step = "select-method" | "enter-contact" | "customize";

interface ServiceRegionLeaf {
  id: string;
  region_name: string;
}

interface ComponentGroup {
  component_id: string | null;
  component_name: string | null;
  service_regions: ServiceRegionLeaf[];
}

interface ServiceGroup {
  service_id: string;
  service_name: string;
  components: ComponentGroup[];
}

const METHODS: { id: Method; label: string; icon: string }[] = [
  { id: "email",   label: "Email",    icon: "✉" },
  // Phone, MS Teams, Webhook hidden until implemented
  // { id: "phone",   label: "Phone",    icon: "📱" },
  // { id: "msteams", label: "MS Teams", icon: "🟣" },
  // { id: "webhook", label: "Webhook",  icon: "🔗" },
];

const PLACEHOLDERS: Record<Method, string> = {
  email:   "you@example.com",
  phone:   "+1 (555) 123-4567",
  msteams: "Microsoft Teams URL",
  webhook: "https://your-webhook-url.com/...",
};

const TITLES: Record<Method, string> = {
  email:   "Subscribe for Email Updates",
  phone:   "Subscribe for Phone Updates",
  msteams: "Subscribe for Teams Updates",
  webhook: "Subscribe for Webhook Updates",
};

/** Checkbox that supports the indeterminate state. */
function TriCheckbox({
  checked,
  indeterminate,
  onChange,
  id,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  id?: string;
}) {
  const ref = useCallback(
    (el: HTMLInputElement | null) => {
      if (el) el.indeterminate = indeterminate && !checked;
    },
    [indeterminate, checked],
  );
  return (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={checked}
      onChange={onChange}
    />
  );
}

function allSRIds(svc: ServiceGroup): string[] {
  return svc.components.flatMap((c) => c.service_regions.map((sr) => sr.id));
}

function compSRIds(comp: ComponentGroup): string[] {
  return comp.service_regions.map((sr) => sr.id);
}

export function SubscribePage() {
  const navigate = useNavigate();
  const [step, setStep]               = useState<Step>("select-method");
  const [method, setMethod]           = useState<Method>("email");
  const [contactValue, setContactValue] = useState("");
  const [accepted, setAccepted]       = useState(false);
  const [error, setError]             = useState("");
  const [submitting, setSubmitting]   = useState(false);

  // Customize step state
  const [notifyAll, setNotifyAll]             = useState(true);
  const [minNotifications, setMinNotifications] = useState(false);
  const [services, setServices]               = useState<ServiceGroup[]>([]);
  const [selectedSRs, setSelectedSRs]         = useState<string[]>([]);
  const [filterText, setFilterText]           = useState("");
  // Track which service groups are expanded (default all open)
  const [collapsed, setCollapsed]             = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (step === "customize") {
      apiFetch("/api/v1/subscriptions/services/")
        .then((r) => r.json())
        .then(setServices)
        .catch(() => {});
    }
  }, [step]);

  function handleMethodSelect(m: Method) {
    setMethod(m);
    setContactValue("");
    setAccepted(false);
    setError("");
    setStep("enter-contact");
  }

  function handleNext() {
    if (!contactValue.trim()) { setError("This field is required."); return; }
    if (method === "email" && !contactValue.includes("@")) {
      setError("Enter a valid email address."); return;
    }
    if (
      (method === "msteams" || method === "webhook") &&
      !contactValue.startsWith("https://")
    ) {
      setError("The URL must start with https://."); return;
    }
    if (!accepted) { setError("You must accept the terms & services."); return; }
    setError("");
    setStep("customize");
  }

  // ── Selection helpers ──────────────────────────────────────────────────────

  function toggleService(svc: ServiceGroup) {
    const ids = allSRIds(svc);
    const allSel = ids.every((id) => selectedSRs.includes(id));
    setSelectedSRs((prev) =>
      allSel ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])],
    );
  }

  function toggleComponent(comp: ComponentGroup) {
    const ids = compSRIds(comp);
    const allSel = ids.every((id) => selectedSRs.includes(id));
    setSelectedSRs((prev) =>
      allSel ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])],
    );
  }

  function toggleSR(id: string) {
    setSelectedSRs((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function toggleCollapsed(serviceId: string) {
    setCollapsed((prev) => ({ ...prev, [serviceId]: !prev[serviceId] }));
  }

  async function handleSave() {
    if (!notifyAll && selectedSRs.length === 0) {
      setError("Select at least one service."); return;
    }
    setSubmitting(true);
    setError("");
    try {
      const body: Record<string, unknown> = {
        method,
        contact_value: contactValue.trim(),
        notify_all: notifyAll,
        min_notifications: minNotifications,
      };
      if (!notifyAll) body.service_region_ids = selectedSRs;
      const r = await apiFetch("/api/v1/subscriptions/", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(Object.values(d).flat().join(" ") || `HTTP ${r.status}`);
      }
      navigate("/status/subscribe/confirm?confirmed=true");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Subscription failed.");
    } finally {
      setSubmitting(false);
    }
  }

  const filteredServices = services.filter(
    (s) =>
      !filterText ||
      s.service_name.toLowerCase().includes(filterText.toLowerCase()) ||
      s.components.some((c) =>
        c.component_name?.toLowerCase().includes(filterText.toLowerCase()),
      ),
  );

  const breadcrumb = (
    <nav className="subscribe-breadcrumb">
      <Link to="/status">Status Page</Link>
      <span className="subscribe-breadcrumb__sep"> &gt; </span>
      {step === "select-method" ? (
        <span>Subscriptions</span>
      ) : (
        <Link to="#" onClick={() => setStep("select-method")}>Subscriptions</Link>
      )}
      {step === "enter-contact" && (
        <>
          <span className="subscribe-breadcrumb__sep"> &gt; </span>
          <span>{METHODS.find((m) => m.id === method)?.label}</span>
        </>
      )}
      {step === "customize" && (
        <>
          <span className="subscribe-breadcrumb__sep"> &gt; </span>
          <Link to="#" onClick={() => setStep("enter-contact")}>
            {METHODS.find((m) => m.id === method)?.label}
          </Link>
          <span className="subscribe-breadcrumb__sep"> &gt; </span>
          <span>Customizations</span>
        </>
      )}
    </nav>
  );

  return (
    <main className="subscribe-page">
      <header className="subscribe-header">
        <img src="https://stshrnextgenstorage.blob.core.windows.net/aptean-theme/Aptean-logo-login.png" alt="Aptean" className="subscribe-header__logo" />
      </header>
      {breadcrumb}

      <div className="subscribe-card">

        {/* ── Step 1: Select method ── */}
        {step === "select-method" && (
          <>
            <h1 className="subscribe-card__title">Subscribe for Updates</h1>
            <p className="subscribe-card__subtitle">Select your subscription method</p>
            <div className="subscribe-methods">
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="subscribe-method-btn"
                  onClick={() => handleMethodSelect(m.id)}
                >
                  <span className="subscribe-method-btn__icon">{m.icon}</span>
                  <span className="subscribe-method-btn__label">{m.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── Step 2: Enter contact ── */}
        {step === "enter-contact" && (
          <>
            <h1 className="subscribe-card__title">{TITLES[method]}</h1>
            <div className="subscribe-form">
              <input
                type={method === "email" ? "email" : method === "phone" ? "tel" : "url"}
                className={`aptean-input subscribe-input${error ? " subscribe-input--error" : ""}`}
                placeholder={PLACEHOLDERS[method]}
                value={contactValue}
                onChange={(e) => { setContactValue(e.target.value); setError(""); }}
                autoFocus
              />
              {error && <p className="subscribe-error">{error}</p>}
              <label className="subscribe-terms">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                I accept terms &amp; services (
                <a href="#" className="subscribe-terms__link">Read more</a>
                )
              </label>
              <button
                type="button"
                className="aptean-btn aptean-btn--primary subscribe-next-btn"
                onClick={handleNext}
              >
                Next
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Customize ── */}
        {step === "customize" && (
          <>
            <h1 className="subscribe-card__title">Customizations</h1>
            <p className="subscribe-card__subtitle-contact">{contactValue}</p>

            <div className="subscribe-notify-toggle">
              <span>Notify about:</span>
              <label className="subscribe-radio">
                <input
                  type="radio"
                  name="notifyScope"
                  checked={notifyAll}
                  onChange={() => setNotifyAll(true)}
                />
                All services
              </label>
              <label className="subscribe-radio">
                <input
                  type="radio"
                  name="notifyScope"
                  checked={!notifyAll}
                  onChange={() => setNotifyAll(false)}
                />
                Selected services
              </label>
            </div>

            {!notifyAll && (
              <div className="subscribe-services">
                {/* Search bar */}
                <div className="subscribe-services__toolbar">
                  <div className="subscribe-services__search">
                    <span className="subscribe-services__search-icon">🔍</span>
                    <input
                      type="text"
                      placeholder="Filter services..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      className="aptean-input"
                    />
                  </div>
                </div>

                {/* 3-level hierarchy tree */}
                <div className="subscribe-tree">
                  {filteredServices.length === 0 && (
                    <p className="subscribe-tree__empty">No services available.</p>
                  )}
                  {filteredServices.map((svc) => {
                    const svcIds       = allSRIds(svc);
                    const svcAllSel    = svcIds.length > 0 && svcIds.every((id) => selectedSRs.includes(id));
                    const svcSomeSel   = svcIds.some((id) => selectedSRs.includes(id));
                    const isCollapsed  = collapsed[svc.service_id];

                    return (
                      <div key={svc.service_id} className="subscribe-tree__group">

                        {/* ── Level 1: Group (Service) ── */}
                        <div className="subscribe-tree__row subscribe-tree__row--group">
                          <TriCheckbox
                            checked={svcAllSel}
                            indeterminate={svcSomeSel && !svcAllSel}
                            onChange={() => toggleService(svc)}
                          />
                          <button
                            type="button"
                            className="subscribe-tree__expand"
                            onClick={() => toggleCollapsed(svc.service_id)}
                            aria-expanded={!isCollapsed}
                          >
                            {isCollapsed ? "▶" : "▼"}
                          </button>
                          <span className="subscribe-tree__label subscribe-tree__label--group">
                            {svc.service_name}
                          </span>
                        </div>

                        {/* ── Level 2 & 3: Components + Regions ── */}
                        {!isCollapsed && svc.components.map((comp) => {
                          const compIds     = compSRIds(comp);
                          const compAllSel  = compIds.length > 0 && compIds.every((id) => selectedSRs.includes(id));
                          const compSomeSel = compIds.some((id) => selectedSRs.includes(id));
                          const compLabel   = comp.component_name ?? svc.service_name;

                          return (
                            <div key={comp.component_id ?? "direct"} className="subscribe-tree__component-block">

                              {/* ── Level 2: Service Name (Component) ── */}
                              <div className="subscribe-tree__row subscribe-tree__row--service">
                                <TriCheckbox
                                  checked={compAllSel}
                                  indeterminate={compSomeSel && !compAllSel}
                                  onChange={() => toggleComponent(comp)}
                                />
                                <span className="subscribe-tree__label subscribe-tree__label--service">
                                  {compLabel}
                                </span>
                              </div>

                              {/* ── Level 3: Component Name (Region/leaf) ── */}
                              {comp.service_regions.map((sr) => (
                                <label key={sr.id} className="subscribe-tree__row subscribe-tree__row--region">
                                  <input
                                    type="checkbox"
                                    checked={selectedSRs.includes(sr.id)}
                                    onChange={() => toggleSR(sr.id)}
                                  />
                                  <span className="subscribe-tree__label subscribe-tree__label--region">
                                    {sr.region_name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {error && <p className="subscribe-error">{error}</p>}

            <label className="subscribe-checkbox subscribe-min-notif">
              <input
                type="checkbox"
                checked={minNotifications}
                onChange={(e) => setMinNotifications(e.target.checked)}
              />
              Only send me the minimum number of notifications per incident (typically first and final):
            </label>

            <button
              type="button"
              className="aptean-btn aptean-btn--primary subscribe-save-btn"
              onClick={handleSave}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save"}
            </button>
          </>
        )}
      </div>

      <div className="subscribe-footer-links">
        <Link to="/status" className="aptean-btn aptean-btn--ghost">
          Back to Status Page
        </Link>
        {step !== "select-method" && (
          <Link
            to="#"
            onClick={() => {
              if (step === "customize") setStep("enter-contact");
              else setStep("select-method");
            }}
            className="subscribe-back-link"
          >
            Back to{" "}
            {step === "customize" ? METHODS.find((m) => m.id === method)?.label : "Subscriptions"}
          </Link>
        )}
      </div>
    </main>
  );
}
